import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  ChevronLeft, Users, FileText, Settings, Shield, 
  Search, Eye, CheckCircle, XCircle, AlertTriangle, Trash2,
  Building, CreditCard, Save, History, KeyRound
} from 'lucide-react';
import axios from 'axios';
import NotificationBell from '../components/NotificationBell';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_locate-loved-ones-3/artifacts/5wufybp4_LOGO.jpeg";

export default function AdminPage() {
  const { user, token, updateToken } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState({
    government_id_required: false,
    volunteer_network_enabled: false,
    ads_enabled: false
  });
  const [loading, setLoading] = useState(true);
  const [searchUser, setSearchUser] = useState('');
  
  // Settlement Account State
  const [settlementAccount, setSettlementAccount] = useState(null);
  const [settlementHistory, setSettlementHistory] = useState([]);
  const [editingAccount, setEditingAccount] = useState(false);
  const [accountForm, setAccountForm] = useState({
    account_number: '',
    bank_name: '',
    branch_address: '',
    ifsc_code: '',
    micr_code: '',
    branch_code: '',
    account_holder_name: ''
  });
  const [accountSaving, setAccountSaving] = useState(false);
  const [accountSuccess, setAccountSuccess] = useState('');

  // Paytm review seed state
  const [seedingPaytm, setSeedingPaytm] = useState(false);
  const [paytmSeedResult, setPaytmSeedResult] = useState(null);
  const [paytmSeedError, setPaytmSeedError] = useState('');

  // Paytm debug link generator state
  const [debugLinkLoading, setDebugLinkLoading] = useState(false);
  const [debugLinkResult, setDebugLinkResult] = useState(null);
  const [debugLinkError, setDebugLinkError] = useState('');
  const [debugLinkAmount, setDebugLinkAmount] = useState(1);
  const [debugLinkCopied, setDebugLinkCopied] = useState(false);

  const handleSeedPaytmReview = async () => {
    setSeedingPaytm(true);
    setPaytmSeedError('');
    setPaytmSeedResult(null);
    try {
      const res = await axios.post(`${API}/admin/seed-paytm-review`, {}, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setPaytmSeedResult(res.data);
    } catch (err) {
      setPaytmSeedError(err?.response?.data?.detail || 'Failed to seed Paytm review accounts.');
    } finally {
      setSeedingPaytm(false);
    }
  };

  const handleGeneratePaytmDebugLink = async () => {
    setDebugLinkLoading(true);
    setDebugLinkError('');
    setDebugLinkResult(null);
    setDebugLinkCopied(false);
    try {
      const res = await axios.post(
        `${API}/payments/paytm/debug-link`,
        {},
        {
          params: { amount: debugLinkAmount },
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      setDebugLinkResult(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === 'object' && detail?.message) {
        // Amount ladder exhausted — surface full attempt trail
        setDebugLinkError(
          `${detail.message}. Attempts: ${detail.attempts?.map(a => `₹${a.amount} → ${a.status}`).join(', ') || 'none'}`
        );
      } else {
        setDebugLinkError(typeof detail === 'string' ? detail : 'Failed to generate Paytm debug link.');
      }
    } finally {
      setDebugLinkLoading(false);
    }
  };

  const handleCopyDebugLink = async () => {
    if (!debugLinkResult?.checkoutUrl) return;
    try {
      await navigator.clipboard.writeText(debugLinkResult.checkoutUrl);
      setDebugLinkCopied(true);
      setTimeout(() => setDebugLinkCopied(false), 2500);
    } catch (e) {
      // Fallback for insecure contexts
      const ta = document.createElement('textarea');
      ta.value = debugLinkResult.checkoutUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setDebugLinkCopied(true);
      setTimeout(() => setDebugLinkCopied(false), 2500);
    }
  };

  const [debugOrderCopied, setDebugOrderCopied] = useState(false);
  const handleCopyDebugOrderId = async () => {
    if (!debugLinkResult?.orderId) return;
    try {
      await navigator.clipboard.writeText(debugLinkResult.orderId);
    } catch (e) {
      const ta = document.createElement('textarea');
      ta.value = debugLinkResult.orderId;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setDebugOrderCopied(true);
    setTimeout(() => setDebugOrderCopied(false), 2500);
  };

  // Normalize emails state
  const [normalizing, setNormalizing] = useState(false);
  const [normalizeResult, setNormalizeResult] = useState(null);
  const [normalizeError, setNormalizeError] = useState('');

  const handleNormalizeEmails = async () => {
    setNormalizing(true);
    setNormalizeError('');
    setNormalizeResult(null);
    try {
      const res = await axios.post(`${API}/admin/normalize-emails`, {}, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setNormalizeResult(res.data);
    } catch (err) {
      setNormalizeError(err?.response?.data?.detail || 'Failed to normalize emails.');
    } finally {
      setNormalizing(false);
    }
  };

  // Rotate-my-admin-credentials state
  const [rotateEmail, setRotateEmail] = useState('');
  const [rotatePassword, setRotatePassword] = useState('');
  const [rotating, setRotating] = useState(false);
  const [rotateResult, setRotateResult] = useState(null);
  const [rotateError, setRotateError] = useState('');

  const handleRotateAdminCreds = async () => {
    setRotateError('');
    setRotateResult(null);
    if (!rotateEmail.trim() || !rotatePassword.trim()) {
      setRotateError('Please fill in both the new email and the new password.');
      return;
    }
    if (rotatePassword.trim().length < 8) {
      setRotateError('Password must be at least 8 characters.');
      return;
    }
    const confirmed = window.confirm(
      `Rotate your admin login to "${rotateEmail.trim()}"?\n\nAfter this, your CURRENT credentials will stop working immediately. You must sign in again with the new email and password.`
    );
    if (!confirmed) return;

    setRotating(true);
    try {
      const res = await axios.post(
        `${API}/admin/rotate-my-account`,
        { new_email: rotateEmail.trim(), new_password: rotatePassword.trim() },
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      setRotateResult(res.data);
      // Swap in the freshly-issued JWT so the current tab keeps working —
      // every OTHER token/session for this admin is now revoked.
      if (res.data?.token && updateToken) {
        updateToken(res.data.token);
      }
      setRotatePassword('');
    } catch (err) {
      setRotateError(err?.response?.data?.detail || 'Failed to rotate admin credentials.');
    } finally {
      setRotating(false);
    }
  };

  // Password reset requests state
  const [resetRequests, setResetRequests] = useState([]);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetResolved, setResetResolved] = useState({}); // { requestId: { temp_password, email, name } }
  const [resetError, setResetError] = useState('');

  const fetchResetRequests = async () => {
    setResetLoading(true);
    setResetError('');
    try {
      const res = await axios.get(`${API}/admin/password-resets`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setResetRequests(res.data.items || []);
    } catch (err) {
      setResetError(err?.response?.data?.detail || 'Failed to load reset requests.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleResolveReset = async (requestId) => {
    setResetError('');
    try {
      const res = await axios.post(`${API}/admin/password-resets/${requestId}/resolve`, {}, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setResetResolved((prev) => ({ ...prev, [requestId]: res.data }));
      // Drop the resolved row from the open list
      setResetRequests((prev) => prev.filter((r) => r.request_id !== requestId));
    } catch (err) {
      setResetError(err?.response?.data?.detail || 'Could not resolve reset.');
    }
  };

  const handleDismissReset = async (requestId) => {
    setResetError('');
    try {
      await axios.post(`${API}/admin/password-resets/${requestId}/dismiss`, {}, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setResetRequests((prev) => prev.filter((r) => r.request_id !== requestId));
    } catch (err) {
      setResetError(err?.response?.data?.detail || 'Could not dismiss reset.');
    }
  };

  const [accountError, setAccountError] = useState('');
  const [qrStats, setQrStats] = useState(null);
  const [qrRefreshing, setQrRefreshing] = useState(false);

  const fetchQrStats = async () => {
    setQrRefreshing(true);
    try {
      const r = await axios.get(`${API}/admin/qr-stats`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` },
      });
      setQrStats(r.data);
    } catch (e) {
      console.error('Failed to fetch QR stats', e);
    } finally {
      setQrRefreshing(false);
    }
  };

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchData();
    fetchQrStats();
    const t = setInterval(fetchQrStats, 60000);
    return () => clearInterval(t);
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, settingsRes, accountRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { 
          withCredentials: true, 
          headers: { Authorization: `Bearer ${token}` } 
        }),
        axios.get(`${API}/admin/users`, { 
          withCredentials: true, 
          headers: { Authorization: `Bearer ${token}` } 
        }),
        axios.get(`${API}/admin/settings`, { 
          withCredentials: true, 
          headers: { Authorization: `Bearer ${token}` } 
        }),
        axios.get(`${API}/admin/settlement-account`, { 
          withCredentials: true, 
          headers: { Authorization: `Bearer ${token}` } 
        })
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setSettings(settingsRes.data);
      setSettlementAccount(accountRes.data);
      setAccountForm({
        account_number: accountRes.data.account_number || '',
        bank_name: accountRes.data.bank_name || '',
        branch_address: accountRes.data.branch_address || '',
        ifsc_code: accountRes.data.ifsc_code || '',
        micr_code: accountRes.data.micr_code || '',
        branch_code: accountRes.data.branch_code || '',
        account_holder_name: accountRes.data.account_holder_name || ''
      });
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettlementHistory = async () => {
    try {
      const response = await axios.get(`${API}/admin/settlement-history`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettlementHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch settlement history:', error);
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/role?role=${newRole}`, {}, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(users.map(u => u.user_id === userId ? { ...u, role: newRole } : u));
    } catch (error) {
      console.error('Failed to update role:', error);
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(users.filter(u => u.user_id !== userId));
    } catch (error) {
      console.error('Failed to delete user:', error);
    }
  };

  const updateSettings = async (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    
    try {
      await axios.put(`${API}/admin/settings`, newSettings, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Failed to update settings:', error);
    }
  };

  const handleAccountFormChange = (field, value) => {
    setAccountForm({ ...accountForm, [field]: value });
  };

  const saveSettlementAccount = async () => {
    setAccountSaving(true);
    setAccountError('');
    setAccountSuccess('');

    try {
      const response = await axios.put(`${API}/admin/settlement-account`, accountForm, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettlementAccount(response.data.account);
      setAccountSuccess('Settlement account updated successfully!');
      setEditingAccount(false);
      fetchSettlementHistory();
    } catch (error) {
      setAccountError('Failed to update settlement account. Please try again.');
    } finally {
      setAccountSaving(false);
    }
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchUser.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchUser.toLowerCase())
  );

  const getRoleBadge = (role) => {
    const roleConfig = {
      admin: { class: 'bg-red-100 text-red-700', icon: Shield },
      moderator: { class: 'bg-purple-100 text-purple-700', icon: Eye },
      authority: { class: 'bg-blue-100 text-blue-700', icon: Shield },
      user: { class: 'bg-slate-100 text-slate-700', icon: Users }
    };
    const config = roleConfig[role] || roleConfig.user;
    const Icon = config.icon;
    return (
      <Badge className={config.class}>
        <Icon className="w-3 h-3 mr-1" />
        {role}
      </Badge>
    );
  };

  const maskAccountNumber = (num) => {
    if (!num || num.length < 4) return num;
    return '*'.repeat(num.length - 4) + num.slice(-4);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="text-muted-foreground hover:text-primary">
                <ChevronLeft className="w-5 h-5" />
              </Link>
              <Link to="/" className="flex items-center gap-2">
                <img src={LOGO_URL} alt="MISYOU" className="w-8 h-8 rounded-lg object-cover" />
                <span className="text-lg font-black text-slate-900">MISYOU</span>
              </Link>
            </div>
            <div className="flex items-center gap-2">
              <NotificationBell />
              <Badge className="bg-red-100 text-red-700">
                <Shield className="w-3 h-3 mr-1" />
                Admin Panel
              </Badge>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-8">Admin Dashboard</h1>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <Card className="bg-white shadow-lg">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-slate-900">{stats.total_users}</div>
                <div className="text-sm text-muted-foreground">Total Users</div>
              </CardContent>
            </Card>
            <Card className="bg-white shadow-lg">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-slate-900">{stats.total_cases}</div>
                <div className="text-sm text-muted-foreground">Total Cases</div>
              </CardContent>
            </Card>
            <Card className="bg-white shadow-lg">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-red-600">{stats.active_cases}</div>
                <div className="text-sm text-muted-foreground">Active Cases</div>
              </CardContent>
            </Card>
            <Card className="bg-white shadow-lg">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-green-600">{stats.found_cases}</div>
                <div className="text-sm text-muted-foreground">Found Safe</div>
              </CardContent>
            </Card>
            <Card className="bg-white shadow-lg">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-primary">{stats.total_sightings}</div>
                <div className="text-sm text-muted-foreground">Sightings</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Invoice Ledger — quick link for accountant / CA / gateway audits */}
        <Card className="bg-white shadow-lg mb-8" data-testid="invoice-ledger-widget">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-600 text-white flex items-center justify-center shrink-0">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="8" y1="13" x2="16" y2="13" />
                    <line x1="8" y1="17" x2="14" y2="17" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-bold text-slate-900">Invoice Ledger</h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Filter, download and export every invoice across all gateways — for your accountant or gateway audit requests.
                  </p>
                </div>
              </div>
              <Link to="/admin/invoices">
                <Button size="sm" data-testid="open-invoice-ledger-btn">
                  Open ledger →
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Database Backups — daily automated safety net */}
        <Card className="bg-white shadow-lg mb-8" data-testid="backups-widget">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-600 text-white flex items-center justify-center shrink-0">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
                    <ellipse cx="12" cy="5" rx="9" ry="3" />
                    <path d="M3 5v14a9 3 0 0 0 18 0V5" />
                    <path d="M3 12a9 3 0 0 0 18 0" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-bold text-slate-900">Database Backups</h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Automatic ZIP snapshots of every collection at 03:00 IST daily. Trigger manual backups, view history, download any past archive.
                  </p>
                </div>
              </div>
              <Link to="/admin/backups">
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="open-backups-btn">
                  Open backups →
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* QR Scan Widget */}
        <Card className="bg-white shadow-lg mb-8" data-testid="qr-stats-widget">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-900 text-white flex items-center justify-center shrink-0">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
                    <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
                    <rect x="3" y="14" width="7" height="7" /><path d="M14 14h2v2h-2zM18 14h3v3h-3zM14 18h2v3h-2zM18 19h3v2h-3z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 flex items-center gap-2">
                    QR scan tracker
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                      Live
                    </span>
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Counts visits to <code className="text-[10px] bg-slate-100 px-1 py-0.5 rounded">misyou.in?ref=qr</code> from your printed QR codes.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Link to="/qr">
                  <Button size="sm" variant="outline" data-testid="open-qr-page-btn">View / share QR</Button>
                </Link>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={fetchQrStats}
                  disabled={qrRefreshing}
                  data-testid="refresh-qr-stats-btn"
                >
                  {qrRefreshing ? 'Refreshing…' : 'Refresh'}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                { k: 'total', label: 'All time' },
                { k: 'today', label: 'Today' },
                { k: 'last_24h', label: 'Last 24h' },
                { k: 'last_7d', label: 'Last 7 days' },
                { k: 'last_30d', label: 'Last 30 days' },
              ].map((m) => (
                <div key={m.k} className="rounded-lg border border-slate-100 bg-slate-50/50 p-3" data-testid={`qr-stat-${m.k}`}>
                  <p className="text-xs text-slate-500">{m.label}</p>
                  <p className="text-2xl font-bold text-slate-900 tabular-nums">
                    {qrStats ? (qrStats[m.k] ?? 0) : '—'}
                  </p>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-3">
              {qrStats?.last_scan
                ? `Last scan: ${new Date(qrStats.last_scan).toLocaleString()}`
                : 'No scans yet — print and distribute your QR to start tracking.'}
            </p>
          </CardContent>
        </Card>

        <Tabs defaultValue="users" className="space-y-6">
          <TabsList>
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              User Management
            </TabsTrigger>
            <TabsTrigger value="settlement" className="flex items-center gap-2">
              <Building className="w-4 h-4" />
              Settlement Account
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              System Settings
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="bg-white shadow-lg">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl">User Management</CardTitle>
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search users..."
                      value={searchUser}
                      onChange={(e) => setSearchUser(e.target.value)}
                      className="pl-10"
                      data-testid="admin-search-users"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-y border-slate-200">
                      <tr>
                        <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">User</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Role</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Status</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Plan</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredUsers.map((u) => (
                        <tr key={u.user_id} className="hover:bg-slate-50" data-testid={`admin-user-${u.user_id}`}>
                          <td className="px-4 py-3">
                            <div>
                              <div className="font-medium text-slate-900">{u.name}</div>
                              <div className="text-sm text-muted-foreground">{u.email}</div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Select 
                              value={u.role} 
                              onValueChange={(v) => updateUserRole(u.user_id, v)}
                              disabled={u.user_id === user?.user_id}
                            >
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="user">User</SelectItem>
                                <SelectItem value="moderator">Moderator</SelectItem>
                                <SelectItem value="authority">Authority</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {u.verified ? (
                                <Badge className="bg-green-100 text-green-700">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Verified
                                </Badge>
                              ) : (
                                <Badge className="bg-slate-100 text-slate-600">
                                  <XCircle className="w-3 h-3 mr-1" />
                                  Unverified
                                </Badge>
                              )}
                              {u.consent_accepted && (
                                <Badge className="bg-blue-100 text-blue-700">Consent</Badge>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={
                              u.subscription_plan === 'premium' ? 'bg-purple-100 text-purple-700' :
                              u.subscription_plan === 'standard' ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-100 text-slate-700'
                            }>
                              {u.subscription_plan || 'free'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            {u.user_id !== user?.user_id && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => deleteUser(u.user_id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {filteredUsers.length === 0 && (
                  <div className="p-8 text-center text-muted-foreground">
                    No users found
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settlement Account Tab */}
          <TabsContent value="settlement">
            <div className="space-y-6">
              {/* Current Account Display */}
              <Card className="bg-white shadow-lg">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-xl flex items-center gap-2">
                        <Building className="w-5 h-5 text-primary" />
                        Payment Settlement Account
                      </CardTitle>
                      <CardDescription>
                        All subscription payments are settled to this account
                      </CardDescription>
                    </div>
                    <Button
                      variant={editingAccount ? "outline" : "default"}
                      onClick={() => {
                        setEditingAccount(!editingAccount);
                        setAccountError('');
                        setAccountSuccess('');
                      }}
                      data-testid="edit-settlement-btn"
                    >
                      {editingAccount ? 'Cancel' : 'Edit Account'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {accountSuccess && (
                    <Alert className="mb-6 bg-green-50 border-green-200">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <AlertDescription className="text-green-700">{accountSuccess}</AlertDescription>
                    </Alert>
                  )}
                  
                  {accountError && (
                    <Alert variant="destructive" className="mb-6">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>{accountError}</AlertDescription>
                    </Alert>
                  )}

                  {!editingAccount ? (
                    /* View Mode */
                    <div className="bg-slate-50 rounded-xl p-6 space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-muted-foreground text-xs">Account Number</Label>
                          <p className="font-mono font-semibold text-lg">{settlementAccount?.account_number}</p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground text-xs">Bank Name</Label>
                          <p className="font-semibold">{settlementAccount?.bank_name}</p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground text-xs">IFSC Code</Label>
                          <p className="font-mono font-semibold">{settlementAccount?.ifsc_code}</p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground text-xs">MICR Code</Label>
                          <p className="font-mono font-semibold">{settlementAccount?.micr_code || '-'}</p>
                        </div>
                        <div className="md:col-span-2">
                          <Label className="text-muted-foreground text-xs">Branch Address</Label>
                          <p className="font-semibold">{settlementAccount?.branch_address}</p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground text-xs">Branch Code</Label>
                          <p className="font-mono font-semibold">{settlementAccount?.branch_code || '-'}</p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground text-xs">Account Holder</Label>
                          <p className="font-semibold">{settlementAccount?.account_holder_name || '-'}</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Edit Mode */
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="account_number">Account Number *</Label>
                          <Input
                            id="account_number"
                            value={accountForm.account_number}
                            onChange={(e) => handleAccountFormChange('account_number', e.target.value)}
                            placeholder="e.g., 02651000067763"
                            className="mt-1 font-mono"
                            data-testid="input-account-number"
                          />
                        </div>
                        <div>
                          <Label htmlFor="bank_name">Bank Name *</Label>
                          <Input
                            id="bank_name"
                            value={accountForm.bank_name}
                            onChange={(e) => handleAccountFormChange('bank_name', e.target.value)}
                            placeholder="e.g., Punjab and Sindh Bank"
                            className="mt-1"
                            data-testid="input-bank-name"
                          />
                        </div>
                        <div>
                          <Label htmlFor="ifsc_code">IFSC Code *</Label>
                          <Input
                            id="ifsc_code"
                            value={accountForm.ifsc_code}
                            onChange={(e) => handleAccountFormChange('ifsc_code', e.target.value.toUpperCase())}
                            placeholder="e.g., PSIB0000265"
                            className="mt-1 font-mono"
                            data-testid="input-ifsc-code"
                          />
                        </div>
                        <div>
                          <Label htmlFor="micr_code">MICR Code</Label>
                          <Input
                            id="micr_code"
                            value={accountForm.micr_code}
                            onChange={(e) => handleAccountFormChange('micr_code', e.target.value)}
                            placeholder="e.g., 151023102"
                            className="mt-1 font-mono"
                            data-testid="input-micr-code"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <Label htmlFor="branch_address">Branch Address *</Label>
                          <Input
                            id="branch_address"
                            value={accountForm.branch_address}
                            onChange={(e) => handleAccountFormChange('branch_address', e.target.value)}
                            placeholder="e.g., TALWANDI SABO BRANCH (T0265)"
                            className="mt-1"
                            data-testid="input-branch-address"
                          />
                        </div>
                        <div>
                          <Label htmlFor="branch_code">Branch Code</Label>
                          <Input
                            id="branch_code"
                            value={accountForm.branch_code}
                            onChange={(e) => handleAccountFormChange('branch_code', e.target.value)}
                            placeholder="e.g., 0265"
                            className="mt-1 font-mono"
                            data-testid="input-branch-code"
                          />
                        </div>
                        <div>
                          <Label htmlFor="account_holder_name">Account Holder Name</Label>
                          <Input
                            id="account_holder_name"
                            value={accountForm.account_holder_name}
                            onChange={(e) => handleAccountFormChange('account_holder_name', e.target.value)}
                            placeholder="e.g., MISYOU"
                            className="mt-1"
                            data-testid="input-account-holder"
                          />
                        </div>
                      </div>
                      
                      <div className="flex gap-4 pt-4">
                        <Button
                          onClick={saveSettlementAccount}
                          disabled={accountSaving || !accountForm.account_number || !accountForm.bank_name || !accountForm.ifsc_code}
                          className="bg-primary hover:bg-primary/90"
                          data-testid="save-settlement-btn"
                        >
                          <Save className="w-4 h-4 mr-2" />
                          {accountSaving ? 'Saving...' : 'Save Account Details'}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setEditingAccount(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Account History */}
              <Card className="bg-white shadow-lg">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <History className="w-5 h-5" />
                      Settlement Account History
                    </CardTitle>
                    <Button variant="outline" size="sm" onClick={fetchSettlementHistory}>
                      Load History
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {settlementHistory.length === 0 ? (
                    <p className="text-muted-foreground text-center py-4">
                      Click "Load History" to view previous settlement accounts
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {settlementHistory.map((acc, idx) => (
                        <div 
                          key={acc.account_id} 
                          className={`p-4 rounded-lg border ${acc.is_active ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-mono font-semibold">{maskAccountNumber(acc.account_number)}</p>
                              <p className="text-sm text-muted-foreground">{acc.bank_name} - {acc.ifsc_code}</p>
                            </div>
                            <div className="text-right">
                              {acc.is_active && (
                                <Badge className="bg-green-100 text-green-700">Active</Badge>
                              )}
                              <p className="text-xs text-muted-foreground mt-1">
                                {new Date(acc.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card className="bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="text-xl">System Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label className="text-base font-medium">Government ID Verification</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Require users to upload government ID for verification
                    </p>
                  </div>
                  <Switch
                    checked={settings.government_id_required}
                    onCheckedChange={(v) => updateSettings('government_id_required', v)}
                    data-testid="setting-gov-id"
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label className="text-base font-medium">Volunteer Network</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Enable volunteer network features and organized search events
                    </p>
                  </div>
                  <Switch
                    checked={settings.volunteer_network_enabled}
                    onCheckedChange={(v) => updateSettings('volunteer_network_enabled', v)}
                    data-testid="setting-volunteer"
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label className="text-base font-medium">Display Advertisements</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Show advertisements to non-premium users
                    </p>
                  </div>
                  <Switch
                    checked={settings.ads_enabled}
                    onCheckedChange={(v) => updateSettings('ads_enabled', v)}
                    data-testid="setting-ads"
                  />
                </div>

                <div className="pt-4 border-t border-slate-200">
                  <h3 className="font-medium text-slate-900 mb-4">Role Permissions</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg">
                      <Shield className="w-5 h-5 text-red-600" />
                      <div>
                        <span className="font-medium text-red-800">Admin</span>
                        <p className="text-sm text-red-700">Full control - can edit, delete anything, manage settlement account</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                      <Eye className="w-5 h-5 text-purple-600" />
                      <div>
                        <span className="font-medium text-purple-800">Moderator</span>
                        <p className="text-sm text-purple-700">Can view everything, cannot modify data or see settlement account</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                      <Shield className="w-5 h-5 text-blue-600" />
                      <div>
                        <span className="font-medium text-blue-800">Authority</span>
                        <p className="text-sm text-blue-700">Temporary data access, cannot modify</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-slate-100 rounded-lg">
                      <Users className="w-5 h-5 text-slate-600" />
                      <div>
                        <span className="font-medium text-slate-800">User</span>
                        <p className="text-sm text-slate-700">Can upload and modify only their own data</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment-Gateway Review Seed — re-creates dummy reviewer accounts on the current DB */}
                <div className="pt-4 border-t border-slate-200" data-testid="paytm-seed-section">
                  <h3 className="font-medium text-slate-900 mb-2 flex items-center gap-2">
                    <KeyRound className="w-5 h-5 text-blue-600" />
                    Payment-Gateway Reviewer Accounts
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Click once after each production deploy to (re-)create dummy accounts payment-gateway
                    review teams use to verify your website. Seeds <strong>two pairs</strong>: a Paytm-branded
                    pair (for the existing Paytm ticket) and a gateway-neutral pair (share with Cashfree,
                    Razorpay, or any future gateway). Safe to run anytime — it just resets the documented
                    passwords. Run on <strong>misyou.in</strong> after deploying.
                  </p>
                  <Button
                    onClick={handleSeedPaytmReview}
                    disabled={seedingPaytm}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="seed-paytm-btn"
                  >
                    {seedingPaytm ? 'Seeding…' : 'Seed Reviewer Accounts'}
                  </Button>

                  {paytmSeedError && (
                    <Alert variant="destructive" className="mt-3" data-testid="paytm-seed-error">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>{paytmSeedError}</AlertDescription>
                    </Alert>
                  )}

                  {paytmSeedResult && (
                    <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="paytm-seed-success">
                      <p className="font-semibold text-emerald-800 flex items-center gap-2 mb-3">
                        <CheckCircle className="w-5 h-5" />
                        Seed complete — reviewer accounts ready:
                      </p>

                      <div className="space-y-4 text-sm">
                        {/* Gateway-neutral (Cashfree / Razorpay / any future gateway) */}
                        <div className="border border-emerald-300 rounded-lg overflow-hidden">
                          <div className="bg-emerald-600 text-white px-3 py-1.5 text-xs font-semibold uppercase tracking-wider">
                            Share with Cashfree / Razorpay / any new gateway
                          </div>
                          <div className="p-3 space-y-2 bg-white">
                            <div className="bg-slate-50 border border-slate-200 rounded p-2">
                              <p className="font-semibold text-slate-900 text-xs">Regular user</p>
                              <p className="font-mono text-xs mt-1">
                                Email: <span className="select-all">{paytmSeedResult.credentials.generic.user.email}</span>
                              </p>
                              <p className="font-mono text-xs">
                                Password: <span className="select-all">{paytmSeedResult.credentials.generic.user.password}</span>
                              </p>
                            </div>
                            <div className="bg-slate-50 border border-slate-200 rounded p-2">
                              <p className="font-semibold text-slate-900 text-xs">Admin</p>
                              <p className="font-mono text-xs mt-1">
                                Email: <span className="select-all">{paytmSeedResult.credentials.generic.admin.email}</span>
                              </p>
                              <p className="font-mono text-xs">
                                Password: <span className="select-all">{paytmSeedResult.credentials.generic.admin.password}</span>
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Paytm-branded (existing Paytm ticket) */}
                        <div className="border border-slate-200 rounded-lg overflow-hidden">
                          <div className="bg-slate-500 text-white px-3 py-1.5 text-xs font-semibold uppercase tracking-wider">
                            Share with Paytm (existing ticket)
                          </div>
                          <div className="p-3 space-y-2 bg-white">
                            <div className="bg-slate-50 border border-slate-200 rounded p-2">
                              <p className="font-semibold text-slate-900 text-xs">Regular user</p>
                              <p className="font-mono text-xs mt-1">
                                Email: <span className="select-all">{paytmSeedResult.credentials.paytm.user.email}</span>
                              </p>
                              <p className="font-mono text-xs">
                                Password: <span className="select-all">{paytmSeedResult.credentials.paytm.user.password}</span>
                              </p>
                            </div>
                            <div className="bg-slate-50 border border-slate-200 rounded p-2">
                              <p className="font-semibold text-slate-900 text-xs">Admin</p>
                              <p className="font-mono text-xs mt-1">
                                Email: <span className="select-all">{paytmSeedResult.credentials.paytm.admin.email}</span>
                              </p>
                              <p className="font-mono text-xs">
                                Password: <span className="select-all">{paytmSeedResult.credentials.paytm.admin.password}</span>
                              </p>
                            </div>
                          </div>
                        </div>

                        <p className="text-xs text-slate-600">
                          All accounts have <code>consent_accepted=true</code> so reviewers land directly on the dashboard.
                          Status: {paytmSeedResult.results.map(r => `${r.email} (${r.action})`).join(' · ')}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Paytm Debug Link Generator — for Paytm support tickets */}
                <div className="pt-4 border-t border-slate-200" data-testid="paytm-debug-link-section">
                  <h3 className="font-medium text-slate-900 mb-2 flex items-center gap-2">
                    <KeyRound className="w-5 h-5 text-orange-600" />
                    Paytm Debug Link Generator
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Generates a fresh Paytm hosted-checkout URL that <strong>Paytm&apos;s support team can click</strong> to
                    reproduce SVE-003 (or any other issue) on their end. Bypasses plan-price validation so we can offer
                    a small amount. Falls back automatically: <strong>₹1 → ₹5 → ₹10 → ₹20</strong> if Paytm&apos;s MID rejects
                    the requested amount. Link expires in <strong>~30 minutes</strong>.
                  </p>
                  <div className="flex flex-wrap items-end gap-3 mb-3">
                    <div className="flex-shrink-0">
                      <Label className="text-xs text-slate-600">Starting amount (₹)</Label>
                      <Input
                        type="number"
                        min="1"
                        max="1000"
                        step="1"
                        value={debugLinkAmount}
                        onChange={(e) => setDebugLinkAmount(Number(e.target.value) || 1)}
                        className="w-28"
                        data-testid="debug-link-amount-input"
                      />
                    </div>
                    <Button
                      onClick={handleGeneratePaytmDebugLink}
                      disabled={debugLinkLoading}
                      className="bg-orange-600 hover:bg-orange-700 text-white"
                      data-testid="generate-debug-link-btn"
                    >
                      {debugLinkLoading ? 'Generating…' : 'Generate Paytm Debug Link'}
                    </Button>
                  </div>

                  {debugLinkError && (
                    <Alert variant="destructive" className="mt-3" data-testid="debug-link-error">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription className="break-words">{debugLinkError}</AlertDescription>
                    </Alert>
                  )}

                  {debugLinkResult && (
                    <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg" data-testid="debug-link-success">
                      <p className="font-semibold text-orange-900 flex items-center gap-2 mb-3">
                        <CheckCircle className="w-5 h-5" />
                        Fresh debug link ready — link expires in ~30 minutes
                      </p>
                      <div className="space-y-2 text-sm">
                        <div className="bg-white border border-orange-200 rounded p-3">
                          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Checkout URL (paste this into Paytm&apos;s ticket)</p>
                          <p className="font-mono text-xs mt-1 break-all select-all text-slate-900">
                            {debugLinkResult.checkoutUrl}
                          </p>
                          <Button
                            onClick={handleCopyDebugLink}
                            size="sm"
                            variant="outline"
                            className="mt-2"
                            data-testid="copy-debug-link-btn"
                          >
                            {debugLinkCopied ? 'Copied ✓' : 'Copy URL'}
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="bg-white border border-slate-200 rounded p-2">
                            <p className="text-slate-500 font-semibold">Amount</p>
                            <p className="text-slate-900 font-mono">₹{debugLinkResult.amount}</p>
                          </div>
                          <div className="bg-white border border-slate-200 rounded p-2">
                            <p className="text-slate-500 font-semibold">Environment</p>
                            <p className="text-slate-900 font-mono">{debugLinkResult.env}</p>
                          </div>
                          <div className="bg-white border border-slate-200 rounded p-2 col-span-2">
                            <p className="text-slate-500 font-semibold">Order ID</p>
                            <p className="text-slate-900 font-mono text-[11px] break-all select-all">{debugLinkResult.orderId}</p>
                            <Button
                              onClick={handleCopyDebugOrderId}
                              size="sm"
                              variant="outline"
                              className="mt-2"
                              data-testid="copy-debug-order-id-btn"
                            >
                              {debugOrderCopied ? 'Copied ✓' : 'Copy Order ID'}
                            </Button>
                          </div>
                        </div>
                        {debugLinkResult.attempts && debugLinkResult.attempts.length > 1 && (
                          <p className="text-[11px] text-orange-700 mt-2">
                            Ladder used: {debugLinkResult.attempts.map(a => `₹${a.amount}→${a.status}`).join(' · ')}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Normalize Emails — run once after deploying the case-insensitive fix */}
                <div className="pt-4 border-t border-slate-200" data-testid="normalize-emails-section">
                  <h3 className="font-medium text-slate-900 mb-2 flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-600" />
                    Tidy up legacy emails
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Convert any existing user emails that contain capital letters to lowercase.
                    Run this <strong>once</strong> after deploying the case-insensitive login fix.
                    Safe to re-run — no-op if nothing needs fixing.
                  </p>
                  <Button
                    onClick={handleNormalizeEmails}
                    disabled={normalizing}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="normalize-emails-btn"
                  >
                    {normalizing ? 'Normalizing…' : 'Normalize all user emails'}
                  </Button>
                  {normalizeError && (
                    <Alert variant="destructive" className="mt-3" data-testid="normalize-emails-error">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>{normalizeError}</AlertDescription>
                    </Alert>
                  )}
                  {normalizeResult && (
                    <Alert className="mt-3 bg-emerald-50 border-emerald-200" data-testid="normalize-emails-success">
                      <CheckCircle className="h-4 w-4 text-emerald-600" />
                      <AlertDescription className="text-emerald-800">
                        <p className="font-medium">
                          {normalizeResult.fixed_count === 0
                            ? 'Nothing to fix — every email is already lowercase.'
                            : `Normalized ${normalizeResult.fixed_count} email${normalizeResult.fixed_count === 1 ? '' : 's'}.`}
                        </p>
                        {normalizeResult.skipped_count > 0 && (
                          <p className="text-xs mt-1">
                            Skipped {normalizeResult.skipped_count} due to collisions with existing lowercase rows.
                          </p>
                        )}
                      </AlertDescription>
                    </Alert>
                  )}
                </div>

                {/* Rotate MY admin credentials — for the admin currently signed in */}
                <div className="pt-4 border-t border-slate-200" data-testid="rotate-admin-creds-section">
                  <h3 className="font-medium text-slate-900 mb-2 flex items-center gap-2">
                    <KeyRound className="w-5 h-5 text-red-600" />
                    Rotate my admin login
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Change the email and password of <strong>your own admin account</strong> (
                    <code>{user?.email}</code>). Use this the first time you sign into the live app
                    with legacy credentials — set the fresh email/password you want to use from now
                    on, then sign out and sign back in.
                    <br />
                    <span className="text-red-600 font-medium">
                      ⚠ After you click Rotate, your current credentials stop working immediately.
                    </span>
                  </p>
                  <div className="grid gap-3 max-w-md">
                    <div>
                      <Label htmlFor="rotate-email" className="text-xs text-slate-600">
                        New admin email
                      </Label>
                      <Input
                        id="rotate-email"
                        type="email"
                        value={rotateEmail}
                        onChange={(e) => setRotateEmail(e.target.value)}
                        placeholder="e.g. sg6232647@gmail.com"
                        disabled={rotating}
                        data-testid="rotate-email-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="rotate-password" className="text-xs text-slate-600">
                        New admin password (min 8 chars)
                      </Label>
                      <Input
                        id="rotate-password"
                        type="password"
                        value={rotatePassword}
                        onChange={(e) => setRotatePassword(e.target.value)}
                        placeholder="Choose a strong password"
                        disabled={rotating}
                        data-testid="rotate-password-input"
                      />
                    </div>
                    <Button
                      onClick={handleRotateAdminCreds}
                      disabled={rotating || !rotateEmail || !rotatePassword}
                      className="bg-red-600 hover:bg-red-700 text-white w-fit"
                      data-testid="rotate-admin-creds-btn"
                    >
                      {rotating ? 'Rotating…' : 'Rotate my credentials now'}
                    </Button>
                  </div>
                  {rotateError && (
                    <Alert variant="destructive" className="mt-3" data-testid="rotate-admin-creds-error">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>{rotateError}</AlertDescription>
                    </Alert>
                  )}
                  {rotateResult && (
                    <Alert className="mt-3 bg-emerald-50 border-emerald-200" data-testid="rotate-admin-creds-success">
                      <CheckCircle className="h-4 w-4 text-emerald-600" />
                      <AlertDescription className="text-emerald-800">
                        <p className="font-medium">Credentials updated ✓</p>
                        <p className="text-xs mt-1">
                          New admin email: <code className="select-all">{rotateResult.new_email}</code>.
                          Sign out and sign back in with the new email and password.
                        </p>
                      </AlertDescription>
                    </Alert>
                  )}
                </div>

                {/* Password Reset Requests — admin-mediated forgot-password flow */}
                <div className="pt-4 border-t border-slate-200" data-testid="password-reset-section">
                  <h3 className="font-medium text-slate-900 mb-2 flex items-center gap-2">
                    <KeyRound className="w-5 h-5 text-amber-600" />
                    Password Reset Requests
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Users who used "Forgot password" appear here. Click <strong>Generate temp password</strong>,
                    copy it, and email the credentials to the user from <strong>contact@misyou.in</strong>.
                    They will be forced to choose a new password the moment they sign in with it.
                  </p>
                  <Button
                    onClick={fetchResetRequests}
                    disabled={resetLoading}
                    variant="outline"
                    data-testid="load-password-resets-btn"
                  >
                    {resetLoading ? 'Loading…' : 'Load pending requests'}
                  </Button>

                  {resetError && (
                    <Alert variant="destructive" className="mt-3" data-testid="password-reset-error">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>{resetError}</AlertDescription>
                    </Alert>
                  )}

                  {resetRequests.length === 0 && !resetLoading && !resetError && (
                    <p className="text-sm text-slate-500 italic mt-3">No pending reset requests.</p>
                  )}

                  {resetRequests.length > 0 && (
                    <div className="mt-4 space-y-2" data-testid="password-reset-list">
                      {resetRequests.map((req) => (
                        <div
                          key={req.request_id}
                          className="flex items-center justify-between bg-white border border-slate-200 rounded p-3"
                        >
                          <div className="text-sm">
                            <p className="font-medium text-slate-900">{req.name || '—'}</p>
                            <p className="text-xs text-slate-600">{req.email}</p>
                            <p className="text-[10px] text-slate-400 mt-1">
                              {new Date(req.created_at).toLocaleString()}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDismissReset(req.request_id)}
                              data-testid={`dismiss-reset-${req.request_id}`}
                            >
                              Dismiss
                            </Button>
                            <Button
                              size="sm"
                              className="bg-amber-600 hover:bg-amber-700 text-white"
                              onClick={() => handleResolveReset(req.request_id)}
                              data-testid={`resolve-reset-${req.request_id}`}
                            >
                              Generate temp password
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {Object.keys(resetResolved).length > 0 && (
                    <div className="mt-4 space-y-2" data-testid="password-reset-resolved-list">
                      <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                        Just generated — copy &amp; email these to the user
                      </p>
                      {Object.entries(resetResolved).map(([rid, data]) => (
                        <div
                          key={rid}
                          className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm"
                        >
                          <p className="font-semibold text-emerald-900">{data.name || data.email}</p>
                          <p className="font-mono text-xs mt-1">
                            Email: <span className="select-all">{data.email}</span>
                          </p>
                          <p className="font-mono text-xs">
                            Temp password: <span className="select-all">{data.temp_password}</span>
                          </p>
                          <p className="text-[11px] text-emerald-800 mt-2">
                            {data.instructions}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
